import React, { useEffect } from 'react';
import { ContentRendererProps, LandingPageContent } from './types';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Compass, 
  Camera, 
  Map, 
  MessageSquare,
  User,
  Mail,
  Globe,
  Instagram
} from 'lucide-react';
import BrandThemeWrapper from '@/components/brand/BrandThemeWrapper';
import { useBrandData } from '@/components/brand/useBrandData';
import useContentVersion from './useContentVersion';

/**
 * Renderer for landing page content type
 */
export const LandingPageRenderer: React.FC<ContentRendererProps> = ({ content, versionNumber }) => {
  // Use content version hook to get the correct version of the content
  const { versionedContent, refreshVersion } = useContentVersion({ 
    content, 
    versionNumber 
  });
  
  // Force refresh when current_version changes
  useEffect(() => {
    refreshVersion();
  }, [content.current_version, refreshVersion]);
  
  // Use versioned content or fall back to original content
  const currentContent = versionedContent || content;
  
  // Extract landing page data with type safety
  const landingPageContent = currentContent.content_json as LandingPageContent;

  const brandId = currentContent.brand_id;

  const { brand } = useBrandData(brandId);
  const brand_name = brand?.brand_name;
  
  // Extract key elements from the landing page content
  const { 
    headline,
    byline,
    benefits = [],
    testimonials = [],
    faq = [],
    primary_cta,
    secondary_cta,
    closing_statement
  } = landingPageContent;

  landingPageContent.brand_name = brand_name

  // Function to render the appropriate icon based on icon name
  const renderIcon = (iconName: string) => {
    switch (iconName.toLowerCase()) {
      case 'compass':
        return <Compass className="h-6 w-6" />;
      case 'camera':
        return <Camera className="h-6 w-6" />;
      case 'map':
        return <Map className="h-6 w-6" />;
      case 'message':
        return <MessageSquare className="h-6 w-6" />;
      case 'user':
        return <User className="h-6 w-6" />;
      case 'mail':
        return <Mail className="h-6 w-6" />;
      case 'globe':
        return <Globe className="h-6 w-6" />;
      case 'instagram':
        return <Instagram className="h-6 w-6" />;
      default:
        return <Compass className="h-6 w-6" />;
    }
  };

  return (
    <BrandThemeWrapper brandId={content.brand_id} contentType="landing-page">
      <div className="landing-page w-full overflow-auto bg-white dark:bg-gray-900">
        {/* Navigation header */}
      <header className="bg-white dark:bg-gray-900 shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <span className="text-xl font-bold text-gray-900 dark:text-white">{brand_name}</span>
          </div>
          <nav className="hidden md:flex space-x-6">
            <a href="#" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">Home</a>
            <a href="#" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">Features</a>
            <a href="#" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">Testimonials</a>
            <a href="#" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">Contact</a>
          </nav>
          <button style={{ 
            backgroundColor: 'var(--brand-primary, #3b82f6)',
            color: 'white',
            borderRadius: 'var(--border-radius-md, var(--brand-radius-md, 0.25rem))'
          }} className="px-4 py-2 hover:opacity-90 transition-colors">
            Sign Up
          </button>
        </div>
      </header>

      {/* Main content sections */}
      <main>
        {/* Hero Section */}
        <section style={{
          background: `linear-gradient(to bottom right, var(--brand-primary, #3b82f6), var(--brand-secondary, #10b981))`,
          fontFamily: 'var(--font-body, var(--brand-font-primary, inherit))'
        }} className="py-16 px-6 text-white">
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center md:items-stretch text-center md:text-left gap-8">
            {/* Left: Text Content */}
            <div className="flex-1 flex flex-col justify-center items-center md:pr-8 text-center">
              <h1 style={{
                fontFamily: 'var(--font-heading, var(--brand-font-heading, var(--brand-font-primary, inherit)))'
              }} className="text-4xl md:text-5xl font-bold mb-6 w-full">{headline}</h1>
              <p className="text-xl mb-8 max-w-3xl mx-auto md:mx-0 w-full">{byline}</p>
              {primary_cta && (
                <a 
                  href={primary_cta.url || '#'} 
                  style={{
                    backgroundColor: 'white',
                    color: 'var(--brand-primary, #3b82f6)',
                    borderRadius: 'var(--border-radius-full, var(--brand-radius-full, 9999px))',
                    fontFamily: 'var(--font-body, var(--brand-font-primary, inherit))',
                    maxWidth: 340,
                    width: '100%',
                    marginTop: '2rem',
                    display: 'block'
                  }}
                  className="px-8 py-3 font-medium hover:bg-gray-100 transition-colors mx-auto"
                >
                  {primary_cta.text}
                </a>
              )}
            </div>
            {/* Right: Hero Image */}
            {landingPageContent.hero_image_url && (
              <div className="flex-1 flex justify-center items-center mb-8 md:mb-0">
                <img
                  src={landingPageContent.hero_image_url}
                  alt={brand_name || headline || 'Hero image'}
                  className="mx-auto rounded-lg shadow-lg max-h-80 object-contain bg-white dark:bg-gray-800"
                  style={{ maxWidth: '100%', background: 'rgba(255,255,255,0.7)' }}
                />
              </div>
            )}
          </div>
        </section>

        {/* Benefits Section */}
        {benefits.length > 0 && (
          <section className="py-16 px-6 bg-white dark:bg-gray-900">
            <div className="max-w-5xl mx-auto">
              <h2 style={{
                fontFamily: 'var(--font-heading, var(--brand-font-heading, var(--brand-font-primary, inherit)))'
              }} className="text-3xl font-bold text-center mb-12 text-gray-900 dark:text-white">Why Choose Us</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {benefits.map((benefit, index) => (
                  <div 
                    key={index} 
                    style={{
                      backgroundColor: 'var(--bg-light-alt, #F5F7FA)',
                      borderRadius: 'var(--border-radius-lg, var(--brand-radius-lg, 0.5rem))',
                      fontFamily: 'var(--font-body, var(--brand-font-primary, inherit))',
                      padding: 'var(--spacing-md, var(--brand-spacing-md, 1rem))',
                    }}
                    className="dark:bg-gray-800"
                  >
                    <div 
                      style={{
                        backgroundColor: 'color-mix(in srgb, var(--brand-primary, #3b82f6) 10%, white)',
                        color: 'var(--brand-primary, #3b82f6)',
                        borderRadius: 'var(--border-radius-full, var(--brand-radius-full, 9999px))'
                      }}
                      className="w-12 h-12 flex items-center justify-center mb-4 dark:bg-blue-900 dark:text-blue-300"
                    >
                      {renderIcon(benefit.icon)}
                    </div>
                    <h3 
                      style={{
                        color: 'var(--text-primary-light, #212121)',
                        fontFamily: 'var(--font-heading, var(--brand-font-heading, var(--brand-font-primary, inherit)))'
                      }}
                      className="text-xl font-semibold mb-2 dark:text-white"
                    >
                      {benefit.title}
                    </h3>
                    <p 
                      style={{
                        color: 'var(--text-secondary-light, #757575)'
                      }}
                      className="dark:text-gray-300"
                    >
                      {benefit.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Testimonials Section */}
        {testimonials.length > 0 && (
          <section className="py-16 px-6 bg-gray-50 dark:bg-gray-800">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12 text-gray-900 dark:text-white">What Our Customers Say</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="p-6 bg-white dark:bg-gray-900 rounded-lg shadow-sm">
                    <p className="text-gray-600 dark:text-gray-300 italic mb-4">"{testimonial.quote}"</p>
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full mr-3 flex items-center justify-center">
                        <User className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">{testimonial.author}</h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ Section */}
        {faq.length > 0 && (
          <section className="py-16 px-6 bg-white dark:bg-gray-900">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12 text-gray-900 dark:text-white">Frequently Asked Questions</h2>
              
              <div className="space-y-6">
                {faq.map((item, index) => (
                  <Card key={index} className="border-gray-200 dark:border-gray-700">
                    <CardContent className="pt-6">
                      <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">{item.question}</h3>
                      <p className="text-gray-600 dark:text-gray-300">{item.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Call to Action Section */}
        <section style={{
          backgroundColor: 'var(--brand-primary, #3b82f6)',
          fontFamily: 'var(--font-body, var(--brand-font-primary, inherit))',
          padding: 'var(--spacing-xl, var(--brand-spacing-xl, 2rem)) var(--spacing-md, var(--brand-spacing-md, 1rem))'
        }} className="text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 style={{
              fontFamily: 'var(--font-heading, var(--brand-font-heading, var(--brand-font-primary, inherit)))'
            }} className="text-3xl font-bold mb-6">Ready to Begin Your Adventure?</h2>
            <p className="text-xl mb-8">{closing_statement}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {primary_cta && (
                <a 
                  href={primary_cta.url} 
                  style={{
                    backgroundColor: 'white',
                    color: 'var(--brand-primary, #3b82f6)',
                    borderRadius: 'var(--border-radius-full, var(--brand-radius-full, 9999px))',
                    fontFamily: 'var(--font-body, var(--brand-font-primary, inherit))'
                  }}
                  className="inline-block px-8 py-3 font-medium hover:bg-gray-100 transition-colors"
                >
                  {primary_cta.text}
                </a>
              )}
              {secondary_cta && (
                <a 
                  href={secondary_cta.url} 
                  style={{
                    backgroundColor: 'var(--brand-accent, #f97316)',
                    borderRadius: 'var(--border-radius-full, var(--brand-radius-full, 9999px))',
                    fontFamily: 'var(--font-body, var(--brand-font-primary, inherit))'
                  }}
                  className="inline-block px-8 py-3 text-white font-medium border border-white hover:opacity-90 transition-colors"
                >
                  {secondary_cta.text}
                </a>
              )}
            </div>
          </div>
        </section>
      </main>
      
      {/* Footer */}
      <footer style={{
        fontFamily: 'var(--font-body, var(--brand-font-primary, inherit))'
      }} className="bg-gray-900 text-white py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 style={{
                fontFamily: 'var(--font-heading, var(--brand-font-heading, var(--brand-font-primary, inherit)))'
              }} className="text-lg font-semibold mb-4">{currentContent.name}</h3>
              <p className="text-gray-400">Beautiful landing page built with Rainfactory</p>
            </div>
            <div>
              <h4 style={{
                fontFamily: 'var(--font-heading, var(--brand-font-heading, var(--brand-font-primary, inherit)))'
              }} className="text-md font-medium mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Testimonials</a></li>
              </ul>
            </div>
            <div>
              <h4 style={{
                fontFamily: 'var(--font-heading, var(--brand-font-heading, var(--brand-font-primary, inherit)))'
              }} className="text-md font-medium mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Support</a></li>
              </ul>
            </div>
            <div>
              <h4 style={{
                fontFamily: 'var(--font-heading, var(--brand-font-heading, var(--brand-font-primary, inherit)))'
              }} className="text-md font-medium mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>© {new Date().getFullYear()} {currentContent.name}. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
    </BrandThemeWrapper>
  );
};

export default LandingPageRenderer; 